import {
	Button,
	Typography,
	Stack,
	Divider,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	CircularProgress,
	Alert,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	FormGroup,
	FormControlLabel,
	Checkbox,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState, FormEvent, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enAU } from "date-fns/locale";

import globalAxios from "../../../globalAxios";
import { fetchRegularSessions, fetchUserSessions } from "../../../index.js";

import type { RootState } from "../../../store/store";
import {
	setRegularSessionsLoading,
	setRegularSessionsError,
	setRegularSessions,
	addRegularSession,
	updateRegularSession as updateRegularSessionInStore,
	removeRegularSession as removeRegularSessionFromStore,
	selectRegularSessions,
	selectRegularSessionsLoading,
	selectRegularSessionsError,
} from "../../../store/slices/regularSessionsSlice.ts";

type ViewMode = "existing" | "new";
type WeekCycle = "" | "A" | "B" | "C" | "D";
type WeekDay =
	| ""
	| "Monday"
	| "Tuesday"
	| "Wednesday"
	| "Thursday"
	| "Friday"
	| "Saturday"
	| "Sunday";

interface RegularSessionForm {
	surgeon_id: string;
	hospital_id: string;
	week_cycle: WeekCycle;
	week_day: WeekDay;
	am: boolean;
	pm: boolean;
	tw: boolean;
	start_date: string | null;
	end_date: string | null;
	start_time: string;
	end_time: string;
}

interface Surgeon {
	id: number;
	first_name?: string;
	last_name?: string;
	name?: string;
}

interface Hospital {
	id: number;
	name?: string;
	hospital_name?: string;
}

interface RegularSession {
	id: number;
	user_id?: number;
	surgeon_id: number;
	hospital_id: number;
	week_cycle: string;
	week_day?: string;
	am?: boolean;
	pm?: boolean;
	tw?: boolean;
	start_date: string | null;
	end_date: string | null;
	start_time: string;
	end_time: string;
	created_at?: string;
	updated_at?: string;
	surgeon?: Surgeon;
	hospital?: Hospital;
}

const formatDate = (d: Date) => {
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const formatTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const today = new Date();
const todayString = formatDate(today);

const initialForm: RegularSessionForm = {
	surgeon_id: "",
	hospital_id: "",
	week_cycle: "",
	week_day: "",
	am: false,
	pm: false,
	tw: false,
	start_date: todayString,
	end_date: null,
	start_time: "",
	end_time: "",
};

const BLOCK_TIME_RANGES = {
	am: { start: "08:00", end: "12:30" },
	pm: { start: "13:30", end: "18:00" },
	tw: { start: "18:00", end: "22:30" },
} as const;

const extractArray = <T,>(
	label: string,
	res: any,
	paths: Array<(x: any) => any>,
): T[] => {
	console.log(`[RegularSessionsPage] extractArray(${label}) raw:`, res);

	for (const get of paths) {
		const candidate = get(res);
		if (Array.isArray(candidate)) {
			console.log(
				`[RegularSessionsPage] extractArray(${label}) found array length:`,
				candidate.length,
			);
			return candidate as T[];
		}
	}

	console.log(
		`[RegularSessionsPage] extractArray(${label}) no array found, defaulting to []`,
	);
	return [];
};

const getSurgeonLabel = (s?: Surgeon | null) => {
	if (!s) return "";
	return (
		s.name ||
		[s.first_name, s.last_name].filter(Boolean).join(" ") ||
		`Surgeon ${s.id}`
	);
};

const getHospitalLabel = (h?: Hospital | null) => {
	if (!h) return "";
	return h.name || h.hospital_name || `Hospital ${h.id}`;
};

const parseDateStringToDate = (
	value: string | null | undefined,
): Date | null => {
	if (!value) return null;

	const parts = value.split("-");
	if (parts.length !== 3) return null;

	const year = Number(parts[0]);
	const month = Number(parts[1]) - 1;
	const day = Number(parts[2]);

	return new Date(year, month, day);
};

const parseTimeStringToDate = (
	value: string | null | undefined,
): Date | null => {
	if (!value) return null;

	const [h, m] = value.split(":").map(Number);
	if (Number.isNaN(h) || Number.isNaN(m)) return null;

	const d = new Date();
	d.setHours(h, m, 0, 0);
	return d;
};

const renderSessionBlocks = (session: RegularSession) => {
	const blocks: string[] = [];
	if (session.am) blocks.push("AM");
	if (session.pm) blocks.push("PM");
	if (session.tw) blocks.push("TW");
	return blocks.length > 0 ? blocks.join(", ") : "-";
};

const getAutoTimeRangeFromBlocks = ({
	am,
	pm,
	tw,
}: {
	am: boolean;
	pm: boolean;
	tw: boolean;
}) => {
	const selected: Array<{ start: string; end: string }> = [];

	if (am) selected.push(BLOCK_TIME_RANGES.am);
	if (pm) selected.push(BLOCK_TIME_RANGES.pm);
	if (tw) selected.push(BLOCK_TIME_RANGES.tw);

	if (selected.length === 0) {
		return { start_time: "", end_time: "" };
	}

	return {
		start_time: selected[0].start,
		end_time: selected[selected.length - 1].end,
	};
};

const RegularSessionsPage = () => {
	const dispatch = useDispatch();

	const regularSessions = useSelector((state: RootState) =>
		selectRegularSessions(state),
	);
	const loadingRegularSessions = useSelector((state: RootState) =>
		selectRegularSessionsLoading(state),
	);
	const regularSessionsError = useSelector((state: RootState) =>
		selectRegularSessionsError(state),
	);

	const [view, setView] = useState<ViewMode>("existing");
	const [editingId, setEditingId] = useState<number | null>(null);

	const [form, setForm] = useState<RegularSessionForm>(initialForm);

	const [surgeons, setSurgeons] = useState<Surgeon[]>([]);
	const [hospitals, setHospitals] = useState<Hospital[]>([]);

	const [loadingSurgeons, setLoadingSurgeons] = useState(false);
	const [loadingHospitals, setLoadingHospitals] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState<number | null>(null);

	const [saveSuccess, setSaveSuccess] = useState<string>("");
	const [saveError, setSaveError] = useState<string>("");

	const [startDate, setStartDate] = useState<Date | null>(today);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [startTime, setStartTime] = useState<Date | null>(null);
	const [endTime, setEndTime] = useState<Date | null>(null);

	const loadRegularSessions = async () => {
		console.log("[RegularSessionsPage] loadRegularSessions start");

		try {
			dispatch(setRegularSessionsLoading(true));
			dispatch(setRegularSessionsError(null));

			console.log("[RegularSessionsPage] GET /regular-sessions");

			const res = await globalAxios.get("/regular-sessions");

			const arr = extractArray<RegularSession>("regularSessions", res, [
				(x) => x?.data?.regularSessions,
				(x) => x?.data?.regular_sessions,
				(x) => x?.data,
				(x) => x?.regularSessions,
			]);

			dispatch(setRegularSessions(arr));
			console.log(
				"[RegularSessionsPage] regularSessions set:",
				arr.length,
			);
		} catch (err) {
			console.error(
				"[RegularSessionsPage] regular sessions fetch error:",
				err,
			);
			dispatch(setRegularSessions([]));
			dispatch(
				setRegularSessionsError("Failed to load regular sessions."),
			);
		} finally {
			dispatch(setRegularSessionsLoading(false));
			console.log("[RegularSessionsPage] loadRegularSessions finished");
		}
	};

	useEffect(() => {
		const loadDropdowns = async () => {
			console.log("[RegularSessionsPage] loadDropdowns start");

			try {
				setLoadingSurgeons(true);
				console.log("[RegularSessionsPage] GET /surgeons");

				const res = await globalAxios.get("/surgeons");

				const arr = extractArray<Surgeon>("surgeons", res, [
					(x) => x?.data?.surgeons,
					(x) => x?.data,
					(x) => x?.surgeons,
				]);

				setSurgeons(arr);
				console.log("[RegularSessionsPage] surgeons set:", arr.length);
			} catch (err) {
				console.error(
					"[RegularSessionsPage] surgeons fetch error:",
					err,
				);
				setSurgeons([]);
			} finally {
				setLoadingSurgeons(false);
				console.log("[RegularSessionsPage] surgeons fetch finished");
			}

			try {
				setLoadingHospitals(true);
				console.log("[RegularSessionsPage] GET /hospitals");

				const res = await globalAxios.get("/hospitals");

				const arr = extractArray<Hospital>("hospitals", res, [
					(x) => x?.data?.hospitals,
					(x) => x?.data,
					(x) => x?.hospitals,
				]);

				setHospitals(arr);
				console.log("[RegularSessionsPage] hospitals set:", arr.length);
			} catch (err) {
				console.error(
					"[RegularSessionsPage] hospitals fetch error:",
					err,
				);
				setHospitals([]);
			} finally {
				setLoadingHospitals(false);
				console.log("[RegularSessionsPage] hospitals fetch finished");
			}

			console.log("[RegularSessionsPage] loadDropdowns complete");
		};

		loadDropdowns();
		loadRegularSessions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleView = () => {
		console.log("[RegularSessionsPage] toggleView clicked");

		setView((prev) => {
			const next = prev === "existing" ? "new" : "existing";
			console.log("[RegularSessionsPage] view after:", next);

			if (next === "existing") {
				setSaveSuccess("");
				setSaveError("");
			}

			return next;
		});
	};

	const handleSelectChange =
		(field: "surgeon_id" | "hospital_id") =>
		(e: SelectChangeEvent<string>) => {
			const value = e.target.value;

			console.log("[RegularSessionsPage] select change:", field, value);

			setForm((prev) => ({
				...prev,
				[field]: value,
			}));
		};

	const handleWeekCycleChange = (value: WeekCycle) => {
		console.log("[RegularSessionsPage] week_cycle:", value);

		setForm((prev) => ({
			...prev,
			week_cycle: value,
		}));
	};

	const handleWeekDayChange = (value: WeekDay) => {
		console.log("[RegularSessionsPage] week_day:", value);

		setForm((prev) => ({
			...prev,
			week_day: value,
		}));
	};

	const handleBlockChange =
		(field: "am" | "pm" | "tw") => (e: ChangeEvent<HTMLInputElement>) => {
			const checked = e.target.checked;

			console.log("[RegularSessionsPage] block change:", field, checked);

			setForm((prev) => {
				const next = {
					...prev,
					[field]: checked,
				};

				const autoRange = getAutoTimeRangeFromBlocks({
					am: next.am,
					pm: next.pm,
					tw: next.tw,
				});

				const finalNext = {
					...next,
					start_time: autoRange.start_time || next.start_time,
					end_time: autoRange.end_time || next.end_time,
				};

				setStartTime(parseTimeStringToDate(finalNext.start_time));
				setEndTime(parseTimeStringToDate(finalNext.end_time));

				return finalNext;
			});
		};

	const handleStartDate = (value: Date | null) => {
		console.log("[RegularSessionsPage] start_date:", value);

		setStartDate(value);

		setForm((prev) => ({
			...prev,
			start_date: value ? formatDate(value) : null,
		}));
	};

	const handleEndDate = (value: Date | null) => {
		console.log("[RegularSessionsPage] end_date:", value);

		setEndDate(value);

		setForm((prev) => ({
			...prev,
			end_date: value ? formatDate(value) : null,
		}));
	};

	const handleStartTime = (value: Date | null) => {
		console.log("[RegularSessionsPage] start_time:", value);

		setStartTime(value);

		setForm((prev) => ({
			...prev,
			start_time: value ? formatTime(value) : "",
		}));
	};

	const handleEndTime = (value: Date | null) => {
		console.log("[RegularSessionsPage] end_time:", value);

		setEndTime(value);

		setForm((prev) => ({
			...prev,
			end_time: value ? formatTime(value) : "",
		}));
	};

	const resetForm = () => {
		console.log("[RegularSessionsPage] resetForm start");

		setForm(initialForm);
		setEditingId(null);
		setStartDate(new Date());
		setEndDate(null);
		setStartTime(null);
		setEndTime(null);

		console.log("[RegularSessionsPage] resetForm complete");
	};

	const validateForm = () => {
		console.log("[RegularSessionsPage] validateForm start");

		if (!form.surgeon_id) return "Please select a surgeon.";
		if (!form.hospital_id) return "Please select a hospital.";
		if (!form.week_cycle) return "Please select a week cycle.";
		if (!form.week_day) return "Please select a week day.";
		if (!form.am && !form.pm && !form.tw) {
			return "Please select at least one of AM, PM, or TW.";
		}
		if (!form.start_time) return "Please select a start time.";
		if (!form.end_time) return "Please select an end time.";

		console.log("[RegularSessionsPage] validateForm passed");
		return "";
	};

	const handleEditRegularSession = (session: RegularSession) => {
		console.log("[RegularSessionsPage] handleEditRegularSession:", session);

		setSaveSuccess("");
		setSaveError("");
		setEditingId(session.id);

		setForm({
			surgeon_id: String(session.surgeon_id),
			hospital_id: String(session.hospital_id),
			week_cycle: session.week_cycle as WeekCycle,
			week_day: (session.week_day || "") as WeekDay,
			am: Boolean(session.am),
			pm: Boolean(session.pm),
			tw: Boolean(session.tw),
			start_date: session.start_date,
			end_date: session.end_date,
			start_time: session.start_time,
			end_time: session.end_time,
		});

		setStartDate(parseDateStringToDate(session.start_date));
		setEndDate(parseDateStringToDate(session.end_date));
		setStartTime(parseTimeStringToDate(session.start_time));
		setEndTime(parseTimeStringToDate(session.end_time));

		setView("new");
	};

	const handleDeleteRegularSession = async (id: number) => {
		console.log("[RegularSessionsPage] handleDeleteRegularSession:", id);

		const confirmed = window.confirm(
			"Are you sure you want to delete this regular session?",
		);

		if (!confirmed) {
			console.log("[RegularSessionsPage] delete cancelled");
			return;
		}

		try {
			setDeletingId(id);
			dispatch(setRegularSessionsError(null));

			console.log(
				`[RegularSessionsPage] DELETE /regular-session/${id} start`,
			);

			await globalAxios.delete(`/regular-sessions/${id}`);

			console.log("[RegularSessionsPage] delete success");

			dispatch(removeRegularSessionFromStore(id));

			if (editingId === id) {
				console.log(
					"[RegularSessionsPage] deleted item was being edited, resetting form",
				);
				resetForm();
			}

			await fetchRegularSessions(dispatch);
			await fetchUserSessions(dispatch);
		} catch (err: any) {
			console.error("[RegularSessionsPage] delete failed:", err);

			const message =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				"Failed to delete regular session.";

			dispatch(setRegularSessionsError(message));
		} finally {
			setDeletingId(null);
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		console.log("[RegularSessionsPage] submit clicked");
		console.log("[RegularSessionsPage] form:", form);
		console.log("[RegularSessionsPage] editingId:", editingId);

		setSaveSuccess("");
		setSaveError("");

		const validationError = validateForm();
		if (validationError) {
			console.log(
				"[RegularSessionsPage] validation failed:",
				validationError,
			);
			setSaveError(validationError);
			return;
		}

		const payload = {
			...form,
			surgeon_id: Number(form.surgeon_id),
			hospital_id: Number(form.hospital_id),
		};

		console.log("[RegularSessionsPage] payload prepared:", payload);

		try {
			setSaving(true);

			if (editingId) {
				console.log(
					`[RegularSessionsPage] PUT /regular-session/${editingId} start`,
				);

				const res = await globalAxios.put(
					`/regular-sessions/${editingId}`,
					payload,
				);

				console.log("[RegularSessionsPage] update success:", res.data);

				dispatch(
					updateRegularSessionInStore({
						id: editingId,
						updates: res.data,
					}),
				);

				await fetchRegularSessions(dispatch);
				setSaveSuccess("Regular session updated successfully.");
			} else {
				console.log(
					"[RegularSessionsPage] POST /regular-session start",
				);

				const res = await globalAxios.post(
					"/regular-sessions",
					payload,
				);

				console.log("[RegularSessionsPage] create success:", res.data);

				dispatch(addRegularSession(res.data));

				await fetchRegularSessions(dispatch);
				setSaveSuccess("Regular session saved successfully.");
			}

			resetForm();
			setView("existing");
		} catch (err: any) {
			console.error("[RegularSessionsPage] save failed:", err);

			const message =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				"Failed to save regular session.";

			setSaveError(message);
		} finally {
			setSaving(false);
			console.log("[RegularSessionsPage] save finished");
		}
	};

	const renderDateValue = (value: string | null | undefined) =>
		value || "Indefinite";

	const renderSurgeonName = (session: RegularSession) => {
		if (session.surgeon) {
			return (
				getSurgeonLabel(session.surgeon) || String(session.surgeon_id)
			);
		}

		const match = surgeons.find((s) => s.id === session.surgeon_id);
		return getSurgeonLabel(match) || String(session.surgeon_id);
	};

	const renderHospitalName = (session: RegularSession) => {
		if (session.hospital) {
			return (
				getHospitalLabel(session.hospital) ||
				String(session.hospital_id)
			);
		}

		const match = hospitals.find((h) => h.id === session.hospital_id);
		return getHospitalLabel(match) || String(session.hospital_id);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
			<Stack spacing={1.5}>
				<Button
					variant="contained"
					disableElevation
					onClick={toggleView}
				>
					{view === "existing"
						? "Create New Regular Session"
						: "View / Modify Existing Regular Sessions"}
				</Button>

				<Divider />

				<Typography variant="body1">
					{view === "existing"
						? "Existing Regular Sessions"
						: editingId
							? "Modify Regular Session"
							: "Create New Regular Session"}
				</Typography>

				{view === "existing" ? (
					<Stack spacing={1.5}>
						{regularSessionsError ? (
							<Alert severity="error">
								{regularSessionsError}
							</Alert>
						) : null}

						<Button
							variant="outlined"
							onClick={loadRegularSessions}
							disabled={loadingRegularSessions}
							sx={{ alignSelf: "flex-start" }}
						>
							{loadingRegularSessions
								? "Refreshing..."
								: "Refresh"}
						</Button>

						{loadingRegularSessions ? (
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
							>
								<CircularProgress size={18} />
								<Typography variant="body2">
									Loading regular sessions...
								</Typography>
							</Stack>
						) : regularSessions.length === 0 ? (
							<Alert severity="info">
								No regular sessions found.
							</Alert>
						) : (
							<TableContainer
								component={Paper}
								variant="outlined"
							>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>ID</TableCell>
											<TableCell>Surgeon</TableCell>
											<TableCell>Hospital</TableCell>
											<TableCell>Week</TableCell>
											<TableCell>Day</TableCell>
											<TableCell>Blocks</TableCell>
											<TableCell>Start Date</TableCell>
											<TableCell>End Date</TableCell>
											<TableCell>Start Time</TableCell>
											<TableCell>End Time</TableCell>
											<TableCell>Actions</TableCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{regularSessions.map((session) => (
											<TableRow key={session.id} hover>
												<TableCell>
													{session.id}
												</TableCell>
												<TableCell>
													{renderSurgeonName(session)}
												</TableCell>
												<TableCell>
													{renderHospitalName(
														session,
													)}
												</TableCell>
												<TableCell>
													{session.week_cycle}
												</TableCell>
												<TableCell>
													{session.week_day || "-"}
												</TableCell>
												<TableCell>
													{renderSessionBlocks(
														session,
													)}
												</TableCell>
												<TableCell>
													{renderDateValue(
														session.start_date,
													)}
												</TableCell>
												<TableCell>
													{renderDateValue(
														session.end_date,
													)}
												</TableCell>
												<TableCell>
													{session.start_time}
												</TableCell>
												<TableCell>
													{session.end_time}
												</TableCell>
												<TableCell>
													<Stack
														direction="row"
														spacing={0.5}
													>
														<IconButton
															size="small"
															onClick={() =>
																handleEditRegularSession(
																	session,
																)
															}
															disabled={
																deletingId ===
																session.id
															}
														>
															<EditIcon fontSize="small" />
														</IconButton>

														<IconButton
															size="small"
															onClick={() =>
																handleDeleteRegularSession(
																	session.id,
																)
															}
															disabled={
																deletingId ===
																session.id
															}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Stack>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Stack>
				) : (
					<form onSubmit={handleSubmit}>
						<Stack spacing={1.5} sx={{ maxWidth: 420 }}>
							{saveSuccess ? (
								<Alert severity="success">{saveSuccess}</Alert>
							) : null}
							{saveError ? (
								<Alert severity="error">{saveError}</Alert>
							) : null}

							<FormControl size="small" fullWidth>
								<InputLabel>Surgeon</InputLabel>
								<Select
									value={form.surgeon_id}
									label="Surgeon"
									onChange={handleSelectChange("surgeon_id")}
								>
									<MenuItem value="">
										<em>Select surgeon</em>
									</MenuItem>

									{loadingSurgeons ? (
										<MenuItem disabled>
											<CircularProgress size={18} />
										</MenuItem>
									) : (
										surgeons.map((s) => (
											<MenuItem
												key={s.id}
												value={String(s.id)}
											>
												{getSurgeonLabel(s)}
											</MenuItem>
										))
									)}
								</Select>
							</FormControl>

							<FormControl size="small" fullWidth>
								<InputLabel>Hospital</InputLabel>
								<Select
									value={form.hospital_id}
									label="Hospital"
									onChange={handleSelectChange("hospital_id")}
								>
									<MenuItem value="">
										<em>Select hospital</em>
									</MenuItem>

									{loadingHospitals ? (
										<MenuItem disabled>
											<CircularProgress size={18} />
										</MenuItem>
									) : (
										hospitals.map((h) => (
											<MenuItem
												key={h.id}
												value={String(h.id)}
											>
												{getHospitalLabel(h)}
											</MenuItem>
										))
									)}
								</Select>
							</FormControl>

							<FormControl size="small" fullWidth>
								<InputLabel>Week Cycle</InputLabel>
								<Select
									value={form.week_cycle}
									label="Week Cycle"
									onChange={(e) =>
										handleWeekCycleChange(
											e.target.value as WeekCycle,
										)
									}
								>
									<MenuItem value="">
										<em>Select week</em>
									</MenuItem>
									<MenuItem value="A">A</MenuItem>
									<MenuItem value="B">B</MenuItem>
									<MenuItem value="C">C</MenuItem>
									<MenuItem value="D">D</MenuItem>
								</Select>
							</FormControl>

							<FormControl size="small" fullWidth>
								<InputLabel>Week Day</InputLabel>
								<Select
									value={form.week_day}
									label="Week Day"
									onChange={(e) =>
										handleWeekDayChange(
											e.target.value as WeekDay,
										)
									}
								>
									<MenuItem value="">
										<em>Select day</em>
									</MenuItem>
									<MenuItem value="Monday">Monday</MenuItem>
									<MenuItem value="Tuesday">Tuesday</MenuItem>
									<MenuItem value="Wednesday">
										Wednesday
									</MenuItem>
									<MenuItem value="Thursday">
										Thursday
									</MenuItem>
									<MenuItem value="Friday">Friday</MenuItem>
									<MenuItem value="Saturday">
										Saturday
									</MenuItem>
									<MenuItem value="Sunday">Sunday</MenuItem>
								</Select>
							</FormControl>

							<FormControl component="fieldset" size="small">
								<Typography variant="body2">
									Session Blocks
								</Typography>
								<FormGroup row>
									<FormControlLabel
										control={
											<Checkbox
												size="small"
												checked={!!form.am}
												onChange={handleBlockChange(
													"am",
												)}
											/>
										}
										label="AM"
									/>
									<FormControlLabel
										control={
											<Checkbox
												size="small"
												checked={!!form.pm}
												onChange={handleBlockChange(
													"pm",
												)}
											/>
										}
										label="PM"
									/>
									<FormControlLabel
										control={
											<Checkbox
												size="small"
												checked={!!form.tw}
												onChange={handleBlockChange(
													"tw",
												)}
											/>
										}
										label="TW"
									/>
								</FormGroup>
							</FormControl>

							<DesktopDatePicker
								label="Start Date"
								value={startDate}
								onChange={handleStartDate}
								format="yyyy-MM-dd"
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
									},
								}}
							/>

							<DesktopDatePicker
								label="End Date"
								value={endDate}
								onChange={handleEndDate}
								format="yyyy-MM-dd"
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
									},
								}}
							/>

							<TimePicker
								label="Start Time"
								value={startTime}
								onChange={handleStartTime}
								ampm={false}
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
									},
								}}
							/>

							<TimePicker
								label="End Time"
								value={endTime}
								onChange={handleEndTime}
								ampm={false}
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
									},
								}}
							/>

							<Stack direction="row" spacing={1}>
								<Button
									type="submit"
									variant="contained"
									disableElevation
									disabled={saving}
								>
									{saving
										? editingId
											? "Updating..."
											: "Saving..."
										: editingId
											? "Update Regular Session"
											: "Save Regular Session"}
								</Button>

								<Button
									variant="outlined"
									onClick={() => {
										resetForm();
										setSaveSuccess("");
										setSaveError("");
									}}
									disabled={saving}
								>
									Reset
								</Button>
							</Stack>
						</Stack>
					</form>
				)}
			</Stack>
		</LocalizationProvider>
	);
};

export default RegularSessionsPage;
